import React, { useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";

function AvailabilityCalendar({
  slots = [],
  selected,
  onSelect,
  onMonthChange,
  className = "",
}) {
  const defaultMonth = useMemo(() => {
    if (!slots.length) return new Date();

    const sorted = [...slots].sort((a, b) =>
      a.slotDate.localeCompare(b.slotDate)
    );

    return new Date(sorted[0].slotDate);
  }, [slots]);

  const priceMap = useMemo(() => {
    return slots.reduce((acc, item) => {
      acc[item.slotDate] = {
        slotKey: item.slotKey,
        slotDate: item.slotDate,
        available: item.availableCapacity > 0,
        availableCapacity: item.availableCapacity,
        price: item.fromPrice,
        displayPrice: item.displayFromPrice,
        originalPrice: item.fromOriginalPrice,
        displayOriginalPrice: item.displayFromOriginalPrice,
        currency: item.currency,
        startTime: item.startTime,
        endTime: item.endTime,
      };

      return acc;
    }, {});
  }, [slots]);

  const selectedDateString = useMemo(() => {
    if (!selected) return null;

    let slot = slots.find((s) => s.slotKey === selected.slotKey);
    if (!slot) {
      return null;
    }

    return slot.slotDate;
  }, [slots, selected]);

  function parseDateString(dateString) {
    if (!dateString) return undefined;

    const [year, month, day] = dateString.split("-").map(Number);

    return new Date(year, month - 1, day);
  }

  return (
    <DayPicker
      mode="single"
      defaultMonth={defaultMonth}
      selected={parseDateString(selectedDateString)}
      onSelect={(date) => {
        if (!date) {
          onSelect(null);
          return;
        }

        const dateString = [
          date.getFullYear(),
          String(date.getMonth() + 1).padStart(2, "0"),
          String(date.getDate()).padStart(2, "0"),
        ].join("-");

        let selectedSlot = priceMap[dateString];
        if (!selectedSlot || selectedSlot.available === false) {
          return;
        }

        onSelect(selectedSlot);
      }}
      onMonthChange={onMonthChange}
      showOutsideDays
      className={`availability-calendar p-4 rounded-2xl border bg-white ${className}`}
      classNames={{
        months: "flex flex-col",
        month: "space-y-4",
        month_caption: "relative flex items-center justify-center h-10 mb-4",
        caption_label: "text-[20px] font-semibold text-black",
        nav: "absolute inset-0 flex items-center justify-between",
        button_previous: `
          h-10
          w-10
          rounded-xl
          border
          bg-white
          flex
          items-center
          justify-center
          text-gray-500
          shadow-sm
          transition-colors
          hover:bg-gray-50
        `,
        button_next: `
          h-10
          w-10
          rounded-xl
          border
          bg-white
          flex
          items-center
          justify-center
          text-gray-500
          shadow-sm
          transition-colors
          hover:bg-gray-50
        `,
        month_grid: "w-full border-collapse",
        weekday: `
          h-10
          text-sm
          font-medium
          text-gray-400
          text-center
        `,
        day: `
          p-0
          text-center
          align-middle
          h-20
          w-20
        `,
        day_button: `
          w-full
          h-full
          p-0
          bg-transparent
          border-0
          hover:bg-transparent
          focus:bg-transparent
          active:bg-transparent
        `,
        selected: `
          bg-transparent
          hover:bg-transparent
          focus:bg-transparent
        `,
        today: "",
        outside: `
          text-gray-300
        `,
        disabled: `
          text-gray-300
          opacity-50
        `,
      }}
      components={{
        Chevron: ({ orientation, className, size = 20 }) => {
          const Icon = orientation === "left" ? ChevronLeft : ChevronRight;
          return <Icon aria-hidden="true" className={className} size={size} />;
        },
        DayButton: ({ day, modifiers, children, ...buttonProps }) => {
          const date = day.date;
          const key = [
            date.getFullYear(),
            String(date.getMonth() + 1).padStart(2, "0"),
            String(date.getDate()).padStart(2, "0"),
          ].join("-");

          const info = priceMap[key];

          const unavailable = !info || info.available === false;

          return (
            <button {...buttonProps}>
              <span
                style={{
                  width: 28,
                  height: 28,
                  fontSize: 13,
                  padding: "5px",
                  borderRadius: 999,
                  backgroundColor: modifiers.selected
                    ? "rgba(200, 161, 51, 1)"
                    : "transparent",
                  color: modifiers.selected ? "#ffffff" : "#2B2B2B",
                  fontWeight: 600,
                }}
                className="availability-calendar-date flex items-center justify-center transition-colors mb-1 leading-none"
              >
                {date.getDate()}
              </span>

              <span
                style={{
                  fontSize: 10,
                  minHeight: 15,
                  color: "#757575",
                  visibility: unavailable ? "hidden" : "visible",
                }}
              >
                {info?.displayPrice || ""}
              </span>
            </button>
          );
        },
      }}
    />
  );
}

export default AvailabilityCalendar;
